import { FC, ReactNode, useEffect, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { FormattedPrice } from 'dibs-buyer-price';
import { FormattedMessage } from 'dibs-react-intl';

import { useShowPricingGuidance } from './helpers/useShowPricingGuidance';
import { useGetPriceAmountByType } from './pricingGuidanceHelpers';
import {
    useShowEstimatedValueFurnitureTestVariant,
    trackEstimatedValueFurnitureTest,
} from '../helpers/estimatedValueFurnitureTestHelpers';

import { PdpSharedEstimatedValue_item$key } from './__generated__/PdpSharedEstimatedValue_item.graphql';

type Props = {
    item: PdpSharedEstimatedValue_item$key;
    currency: string;
    textClassName: string;
    infoContent: ReactNode;
};

export const PdpSharedEstimatedValue: FC<Props> = ({
    item: itemRef,
    currency,
    textClassName,
    infoContent,
}) => {
    const item = useFragment(
        graphql`
            fragment PdpSharedEstimatedValue_item on Item {
                isFurniture
                ...useShowPricingGuidance_item
                ...pricingGuidanceHelpers_item
                ...estimatedValueFurnitureTestHelpers_item
            }
        `,
        itemRef
    );

    const wasTestTracked = useRef(false);

    const showPricingGuidance = useShowPricingGuidance({ item, currency });

    const showEstimatedValueFurnitureTestVariantCopy = useShowEstimatedValueFurnitureTestVariant({
        item,
    });

    const medianPrice = useGetPriceAmountByType({ item, currency, type: 'MEDIAN' });

    const { isFurniture } = item || {};

    useEffect(() => {
        if (isFurniture && showPricingGuidance && !wasTestTracked.current) {
            trackEstimatedValueFurnitureTest();
            wasTestTracked.current = true;
        }
    }, [isFurniture, showPricingGuidance]);

    if (!showPricingGuidance || !showEstimatedValueFurnitureTestVariantCopy) {
        return null;
    }

    return (
        <div className={classnames(dibsCss.flex, dibsCss.itemsCenter)}>
            <span className={classnames(dibsCss.mrXxsmall, dibsCss.textSatan, textClassName)}>
                <FormattedMessage
                    id="PdpSharedEstimatedValue.estimate"
                    defaultMessage="1stDibs Estimate: {price}"
                    values={{
                        price: <FormattedPrice value={medianPrice} currency={currency} />,
                    }}
                />
            </span>
            {infoContent}
        </div>
    );
};
